import { DEFAULT_CURRENCY } from "@/data/constants";
import { toCurrencyCode } from "./toCurrencyCode";

type FormatCurrencyOptions = Intl.NumberFormatOptions & {
  locale?: string;
};

// Building an Intl.NumberFormat is costly and they're immutable, so reuse.
// Unbounded on purpose: keys are locale × currency × the few option sets callers pass
const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (locale: string, options: Intl.NumberFormatOptions) => {
  const key = JSON.stringify([locale, options]);
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, formatter);
  }
  return formatter;
};

/** Formats a major-unit amount as a localized currency string. */
function formatCurrency(
  number: number,
  currency: string = DEFAULT_CURRENCY,
  options: FormatCurrencyOptions = {},
) {
  if (!Number.isFinite(number)) {
    return "Invalid input";
  }

  const currencyCode = toCurrencyCode(currency) ?? DEFAULT_CURRENCY;
  const { locale = "en-US", ...formatOptions } = options;

  try {
    return getFormatter(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...formatOptions,
    }).format(number);
  } catch {
    return `${currency}${number.toFixed(2)}`;
  }
}

export default formatCurrency;
